import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { TurboMotoInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * TURBO MOTO 84 — endless-survival motocross cartridge.
 *
 * Side-view dirt track that scrolls forever and ramps up with distance.
 * Hop between 4 lanes to dodge rocks/rivals and line up ramps; in the air
 * tilt the bike (up/down) to stick a clean flat landing for points + a
 * speed carry — botch the angle and you wipe out.
 *
 * Signature mechanic — HEAT: hold BOOST (X) to go faster, but the engine
 * temperature climbs. Redline it and the engine STALLS, bogging you down
 * until it cools. A soft failure that punishes greedy boosting, distinct
 * from the hard failure of a crash. The whole run is a tug-of-war between
 * pace and temperature.
 *
 * Resolution: 480×270 pixelated, landscape. Endless — the only ending is
 * a wipeout, so it's a pure score-attack: distance + clean landings +
 * rivals passed.
 *
 * Palette: clay dirt-track under a bruised sunset; rider hot-orange, heat
 * gauge sweeping cyan → amber → redline.
 */
const module_: GameModule = {
  id: 'turbo-moto',
  title: 'TURBO MOTO 84',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'racing',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'UP / DOWN', action: 'HOP LANE / TILT IN AIR' },
    { keys: 'X', action: 'BOOST (WATCH THE HEAT)' },
    { keys: 'Z', action: 'START / RESTART' },
  ],
  touch: {
    fireA: 'START',
    fireB: 'BOOST',
  },

  async preload(ctx: PreloadContext): Promise<void> {
    // Hop — short blip on each lane change.
    ctx.registerSfx('hop', {
      wave: 'triangle',
      pitch: [
        { t: 0, f: 520 },
        { t: 18, f: 700 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.14 },
        { t: 18, g: 0 },
      ],
    });
    // Launch — rising whoosh off the ramp lip.
    ctx.registerSfx('launch', {
      wave: 'sawtooth',
      pitch: [
        { t: 0, f: 300 },
        { t: 40, f: 760 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.2 },
        { t: 40, g: 0 },
      ],
    });
    // Clean landing — bright confirming chirp.
    ctx.registerSfx('land-clean', {
      wave: 'square',
      pitch: [
        { t: 0, f: 660 },
        { t: 30, f: 990 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.2 },
        { t: 30, g: 0 },
      ],
    });
    // Stall — the redline "engine bogs" cough: dropping square + noise.
    ctx.registerSfx('stall', {
      wave: 'square',
      pitch: [
        { t: 0, f: 220 },
        { t: 90, f: 70 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 4, g: 0.28 },
        { t: 90, g: 0 },
      ],
    });
    // Crash — noise crunch with a sub thump.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [
        { t: 0, f: 300 },
        { t: 80, f: 90 },
      ],
      gain: [
        { t: 0, g: 0 },
        { t: 3, g: 0.45 },
        { t: 80, g: 0 },
      ],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new TurboMotoInstance(ctx);
  },
};

export default module_;
